import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Settings,
  Key,
  Sparkles,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Trash2,
  Save,
  Zap,
  SlidersHorizontal,
  Users,
  Search,
  UserCog,
  UserPlus,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVIDERS = [
  { id: 'google_gemini', name: 'Google Gemini', description: 'Modelos disponibles se cargan al probar la conexión' },
  { id: 'openai', name: 'OpenAI', description: 'GPT-4o, GPT-4o Mini' },
  { id: 'anthropic', name: 'Anthropic', description: 'Claude Sonnet 4.6, Claude Haiku 4.5' }
];

const DEFAULT_MODELS = {
  openai: [
    { value: 'openai/gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'openai/gpt-4o', label: 'GPT-4o' }
  ],
  anthropic: [
    { value: 'anthropic/claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
    { value: 'anthropic/claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' }
  ]
};

const MUSIC_KEYS = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'Db', 'Ab', 'Eb', 'Bb', 'F'];
const THEMES = ['Adoración', 'Alabanza', 'Avivamiento', 'Gratitud', 'Fe', 'Esperanza', 'Amor', 'Gracia', 'Salvación', 'Victoria', 'Navidad', 'Pascua'];
const EVENTS = ['Apertura', 'Alabanza', 'Adoración', 'Ofrenda', 'Cierre', 'Especial'];

export const SettingsPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  // API Keys
  const [apiKeys, setApiKeys] = useState({ openai: '', anthropic: '', google_gemini: '' });
  const [showKeys, setShowKeys] = useState({ openai: false, anthropic: false, google_gemini: false });
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  // Dynamic models fetched from provider APIs
  const [providerModels, setProviderModels] = useState({});

  // LLM config
  const [activeProvider, setActiveProvider] = useState('');
  const [activeModel, setActiveModel] = useState('');
  const [savingLlm, setSavingLlm] = useState(false);

  // General
  const [generalSettings, setGeneralSettings] = useState({
    default_key: 'G', default_theme: 'Adoración', default_event: 'Alabanza', songs_per_page: 50
  });
  const [savingGeneral, setSavingGeneral] = useState(false);

  // Users management
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSearch, setUsersSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [roleUpdating, setRoleUpdating] = useState({});
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'musician', church: '' });
  const [userFormLoading, setUserFormLoading] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/settings`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.status === 403) {
        toast.error('No tienes permisos de administrador');
        navigate('/dashboard');
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setActiveProvider(data.llm?.active_provider || '');
        setActiveModel(data.llm?.active_model || '');
        if (data.general) setGeneralSettings(data.general);

        // Fetch dynamic models for configured providers
        for (const prov of ['google_gemini', 'openai', 'anthropic']) {
          if (data.api_keys?.[prov]?.configured) {
            try {
              const modelsResp = await fetch(`${API}/settings/list-models`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                credentials: 'include',
                body: JSON.stringify({ provider: prov, api_key: '__stored__' })
              });
              if (modelsResp.ok) {
                const modelsData = await modelsResp.json();
                if (modelsData.models?.length) {
                  setProviderModels(prev => ({ ...prev, [prov]: modelsData.models }));
                }
              }
            } catch {}
          }
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateKey = async (provider) => {
    const key = apiKeys[provider];
    if (!key) { toast.error('Ingresa una API key'); return; }
    setTesting(prev => ({ ...prev, [provider]: true }));
    setTestResults(prev => ({ ...prev, [provider]: undefined }));
    try {
      const resp = await fetch(`${API}/settings/validate-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ provider, api_key: key })
      });
      if (resp.ok) {
        const data = await resp.json();
        setTestResults(prev => ({ ...prev, [provider]: data }));
        if (data.models?.length) {
          setProviderModels(prev => ({ ...prev, [provider]: data.models }));
        }
        if (data.success) {
          toast.success(`Conexión exitosa con ${PROVIDERS.find(p => p.id === provider)?.name}${data.model_tested ? ` (${data.model_tested})` : ''}`);
        } else {
          toast.error(data.error || 'API key inválida');
        }
      } else {
        toast.error('Error al validar');
      }
    } catch (error) {
      setTestResults(prev => ({ ...prev, [provider]: { success: false, error: 'Error de red' } }));
      toast.error('Error de conexión al validar');
    } finally {
      setTesting(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleSaveKey = async (provider) => {
    const key = apiKeys[provider];
    if (!key) { toast.error('Ingresa una API key'); return; }
    setSaving(prev => ({ ...prev, [provider]: true }));
    try {
      const response = await fetch(`${API}/settings/api-key`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ provider, api_key: key })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`API key de ${PROVIDERS.find(p => p.id === provider)?.name} guardada`);
        setApiKeys(prev => ({ ...prev, [provider]: '' }));
        setSettings(prev => ({
          ...prev,
          api_keys: { ...prev?.api_keys, [provider]: { configured: true, masked_key: data.masked_key } }
        }));
        setTestResults(prev => ({ ...prev, [provider]: undefined }));
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar API key');
    } finally {
      setSaving(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleDeleteKey = async (provider) => {
    setSaving(prev => ({ ...prev, [provider]: true }));
    try {
      const response = await fetch(`${API}/settings/api-key/${provider}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('API key eliminada');
        setSettings(prev => ({
          ...prev,
          api_keys: { ...prev?.api_keys, [provider]: { configured: false, masked_key: '' } }
        }));
        setTestResults(prev => ({ ...prev, [provider]: undefined }));
        if (activeProvider === provider) { setActiveProvider(''); setActiveModel(''); }
      }
    } catch (error) {
      toast.error('Error al eliminar API key');
    } finally {
      setSaving(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleTestConnection = async (provider) => {
    setTesting(prev => ({ ...prev, [provider]: true }));
    setTestResults(prev => ({ ...prev, [provider]: undefined }));
    try {
      const response = await fetch(`${API}/settings/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ provider })
      });
      if (response.ok) {
        const data = await response.json();
        setTestResults(prev => ({ ...prev, [provider]: data }));
        if (data.success) {
          toast.success(`Conexión exitosa con ${PROVIDERS.find(p => p.id === provider)?.name}`);
        } else {
          toast.error(`Error: ${data.error}`);
        }
      }
    } catch (error) {
      setTestResults(prev => ({ ...prev, [provider]: { success: false, error: 'Error de red' } }));
      toast.error('Error al probar conexión');
    } finally {
      setTesting(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleSaveLlm = async () => {
    if (!activeProvider || !activeModel) { toast.error('Selecciona un proveedor y modelo'); return; }
    setSavingLlm(true);
    try {
      const response = await fetch(`${API}/settings/llm`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ active_provider: activeProvider, active_model: activeModel })
      });
      if (response.ok) {
        toast.success('Configuración LLM guardada');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar configuración LLM');
    } finally {
      setSavingLlm(false);
    }
  };

  const handleSaveGeneral = async () => {
    setSavingGeneral(true);
    try {
      const response = await fetch(`${API}/settings/general`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(generalSettings)
      });
      if (response.ok) {
        toast.success('Configuración general guardada');
      }
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSavingGeneral(false);
    }
  };

  // Users management functions
  const fetchUsers = async (search = '') => {
    setUsersLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await fetch(`${API}/admin/users${params}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setUsersTotal(data.total);
      }
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setRoleUpdating(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      if (response.ok) {
        toast.success('Rol actualizado');
        setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Error al cambiar rol');
      }
    } catch (error) {
      toast.error('Error al cambiar rol');
    } finally {
      setRoleUpdating(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      const response = await fetch(`${API}/admin/users/${deleteTarget.user_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Usuario eliminado');
        setUsers(prev => prev.filter(u => u.user_id !== deleteTarget.user_id));
        setUsersTotal(prev => prev - 1);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Error al eliminar');
      }
    } catch (error) {
      toast.error('Error al eliminar usuario');
    } finally {
      setDeleteTarget(null);
    }
  };

  const openCreateUser = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', password: '', role: 'musician', church: '' });
    setShowUserModal(true);
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setUserForm({ name: u.name || '', email: u.email || '', password: '', role: u.role || 'musician', church: u.church || '' });
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    setUserFormLoading(true);
    try {
      if (editingUser) {
        // Update
        const body = {};
        if (userForm.name !== editingUser.name) body.name = userForm.name;
        if (userForm.email !== editingUser.email) body.email = userForm.email;
        if (userForm.role !== editingUser.role) body.role = userForm.role;
        if (userForm.church !== (editingUser.church || '')) body.church = userForm.church;
        if (userForm.password) body.password = userForm.password;

        if (Object.keys(body).length === 0) {
          toast.info('No hay cambios para guardar');
          setShowUserModal(false);
          return;
        }

        const res = await fetch(`${API}/admin/users/${editingUser.user_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify(body)
        });
        if (res.ok) {
          toast.success('Usuario actualizado');
          fetchUsers(usersSearch);
          setShowUserModal(false);
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Error al actualizar');
        }
      } else {
        // Create
        if (!userForm.name || !userForm.email || !userForm.password) {
          toast.error('Nombre, email y contraseña son requeridos');
          return;
        }
        const res = await fetch(`${API}/admin/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify(userForm)
        });
        if (res.ok) {
          toast.success('Usuario creado');
          fetchUsers(usersSearch);
          setShowUserModal(false);
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Error al crear usuario');
        }
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setUserFormLoading(false);
    }
  };

  const configuredProviders = PROVIDERS.filter(p => settings?.api_keys?.[p.id]?.configured);

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="h-8 w-64 bg-muted rounded animate-pulse" />
        <div className="h-96 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
          <Settings className="w-8 h-8" />
          Configuración
        </h1>
        <p className="text-muted-foreground">Ajustes internos de la aplicación</p>
      </div>

      <Tabs defaultValue="ai" className="space-y-6" onValueChange={(val) => {
        if (val === 'users' && users.length === 0) fetchUsers();
      }}>
        <TabsList>
          <TabsTrigger value="ai" className="gap-2">
            <Sparkles className="w-4 h-4" />
            Inteligencia Artificial
          </TabsTrigger>
          <TabsTrigger value="general" className="gap-2">
            <SlidersHorizontal className="w-4 h-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Usuarios
          </TabsTrigger>
        </TabsList>

        {/* AI Tab */}
        <TabsContent value="ai" className="space-y-6">
          {/* API Keys Section */}
          <div>
            <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
              <Key className="w-5 h-5" />
              Claves de API
            </h2>
            <div className="space-y-4">
              {PROVIDERS.map(provider => {
                const isConfigured = settings?.api_keys?.[provider.id]?.configured;
                const maskedKey = settings?.api_keys?.[provider.id]?.masked_key;
                const result = testResults[provider.id];

                return (
                  <Card key={provider.id} className="border-border/50">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-heading font-semibold text-lg">{provider.name}</h3>
                          <p className="text-sm text-muted-foreground">{provider.description}</p>
                        </div>
                        <Badge variant={isConfigured ? 'default' : 'secondary'}>
                          {isConfigured ? 'Configurada' : 'No configurada'}
                        </Badge>
                      </div>

                      {isConfigured && (
                        <p className="text-sm text-muted-foreground mb-3">
                          Key actual: <code className="bg-muted px-2 py-0.5 rounded">{maskedKey}</code>
                        </p>
                      )}

                      <div className="flex gap-2 mb-3">
                        <div className="relative flex-1">
                          <Input
                            type={showKeys[provider.id] ? 'text' : 'password'}
                            placeholder={isConfigured ? 'Ingresar nueva key para reemplazar...' : 'Ingresar API key...'}
                            value={apiKeys[provider.id]}
                            onChange={(e) => {
                              setApiKeys(prev => ({ ...prev, [provider.id]: e.target.value }));
                              setTestResults(prev => ({ ...prev, [provider.id]: undefined }));
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                            onClick={() => setShowKeys(prev => ({ ...prev, [provider.id]: !prev[provider.id] }))}
                          >
                            {showKeys[provider.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleValidateKey(provider.id)}
                          disabled={!apiKeys[provider.id] || testing[provider.id]}
                          className="gap-1"
                        >
                          {testing[provider.id] ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Probando...</>
                          ) : (
                            <><Zap className="w-4 h-4" /> Probar conexión</>
                          )}
                        </Button>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleSaveKey(provider.id)}
                          disabled={saving[provider.id] || !apiKeys[provider.id] || !result?.success}
                          size="sm"
                          className="gap-1"
                        >
                          {saving[provider.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Guardar
                        </Button>
                        {isConfigured && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTestConnection(provider.id)}
                            disabled={testing[provider.id]}
                            className="gap-1"
                          >
                            {testing[provider.id] ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> Probando...</>
                            ) : (
                              <><Zap className="w-4 h-4" /> Probar guardada</>
                            )}
                          </Button>
                        )}
                        {isConfigured && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteKey(provider.id)}
                            disabled={saving[provider.id]}
                            className="gap-1 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" /> Eliminar
                          </Button>
                        )}
                      </div>

                      {/* Test Result */}
                      {result && (
                        <div className={`mt-3 p-3 rounded-lg text-sm flex items-start gap-2 ${
                          result.success
                            ? 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200'
                            : 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200'
                        }`}>
                          {result.success ? (
                            <><CheckCircle className="w-4 h-4 mt-0.5 shrink-0" /> Conexión exitosa — {result.model_tested} respondió: "{result.response}"</>
                          ) : (
                            <><XCircle className="w-4 h-4 mt-0.5 shrink-0" /> Error: {result.error}</>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Active LLM Section */}
          <div>
            <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Proveedor LLM Activo
            </h2>
            <Card className="border-border/50">
              <CardContent className="p-5">
                {configuredProviders.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    Configura al menos una API key arriba para activar las funciones de IA.
                  </p>
                ) : (
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Proveedor</Label>
                        <Select value={activeProvider || 'none'} onValueChange={(v) => {
                          const prov = v === 'none' ? '' : v;
                          setActiveProvider(prov);
                          const models = providerModels[prov] || DEFAULT_MODELS[prov] || [];
                          setActiveModel(models[0]?.value || '');
                        }}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar proveedor" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin seleccionar</SelectItem>
                            {configuredProviders.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Modelo</Label>
                        <Select
                          value={activeModel || 'none'}
                          onValueChange={(v) => setActiveModel(v === 'none' ? '' : v)}
                          disabled={!activeProvider}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar modelo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin seleccionar</SelectItem>
                            {(providerModels[activeProvider] || DEFAULT_MODELS[activeProvider] || []).map(m => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button
                      onClick={handleSaveLlm}
                      disabled={savingLlm || !activeProvider || !activeModel}
                      className="gap-2"
                    >
                      {savingLlm ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Guardar configuración LLM
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-6">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SlidersHorizontal className="w-5 h-5" />
                Valores predeterminados
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tonalidad predeterminada</Label>
                  <Select
                    value={generalSettings.default_key || 'G'}
                    onValueChange={(v) => setGeneralSettings(prev => ({ ...prev, default_key: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MUSIC_KEYS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tema predeterminado</Label>
                  <Select
                    value={generalSettings.default_theme || 'Adoración'}
                    onValueChange={(v) => setGeneralSettings(prev => ({ ...prev, default_theme: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {THEMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Evento predeterminado</Label>
                  <Select
                    value={generalSettings.default_event || 'Alabanza'}
                    onValueChange={(v) => setGeneralSettings(prev => ({ ...prev, default_event: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENTS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="max-w-xs space-y-2">
                <Label>Canciones por página</Label>
                <Input
                  type="number"
                  value={generalSettings.songs_per_page || 50}
                  onChange={(e) => setGeneralSettings(prev => ({ ...prev, songs_per_page: parseInt(e.target.value) || 50 }))}
                  min={10}
                  max={200}
                />
              </div>
              <Button onClick={handleSaveGeneral} disabled={savingGeneral} className="gap-2">
                {savingGeneral ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar configuración general
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <UserCog className="w-5 h-5" />
                  Gestión de Usuarios
                </CardTitle>
                <Badge variant="secondary">{usersTotal} usuarios</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search + Create */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nombre o email..."
                    value={usersSearch}
                    onChange={(e) => setUsersSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchUsers(usersSearch)}
                    className="pl-9"
                  />
                </div>
                <Button onClick={openCreateUser} className="gap-2">
                  <UserPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">Nuevo usuario</span>
                </Button>
              </div>

              {/* Table */}
              {usersLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-12 bg-muted rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuario</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Iglesia</TableHead>
                        <TableHead>Registro</TableHead>
                        <TableHead className="w-[100px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(u => (
                        <TableRow key={u.user_id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {u.picture ? (
                                <img src={u.picture} alt={u.name} className="h-8 w-8 rounded-full object-cover" />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                                  {u.name?.charAt(0)?.toUpperCase()}
                                </div>
                              )}
                              <span className="font-medium">{u.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{u.email}</TableCell>
                          <TableCell>
                            <Select
                              value={u.role}
                              onValueChange={(val) => handleRoleChange(u.user_id, val)}
                              disabled={roleUpdating[u.user_id]}
                            >
                              <SelectTrigger className="w-[130px] h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="musician">Músico</SelectItem>
                                <SelectItem value="singer">Cantante</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {u.church || '—'}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString('es') : '—'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditUser(u)}
                                title="Editar usuario"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => setDeleteTarget(u)}
                                title="Eliminar usuario"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {users.length === 0 && !usersLoading && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                            No se encontraron usuarios
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción eliminará permanentemente a <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}).
                  No se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteUser}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Create / Edit User Dialog */}
          <Dialog open={showUserModal} onOpenChange={(open) => !open && setShowUserModal(false)}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Editar usuario' : 'Crear usuario'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Nombre completo</Label>
                  <Input
                    placeholder="Nombre y apellido"
                    value={userForm.name}
                    onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={userForm.email}
                    onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{editingUser ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}</Label>
                  <Input
                    type="password"
                    placeholder={editingUser ? 'Sin cambios' : 'Contraseña'}
                    value={userForm.password}
                    onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rol</Label>
                  <Select
                    value={userForm.role}
                    onValueChange={(val) => setUserForm(prev => ({ ...prev, role: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="musician">Músico</SelectItem>
                      <SelectItem value="singer">Cantante</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Iglesia (opcional)</Label>
                  <Input
                    placeholder="Nombre de la iglesia"
                    value={userForm.church}
                    onChange={(e) => setUserForm(prev => ({ ...prev, church: e.target.value }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowUserModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveUser} disabled={userFormLoading}>
                  {userFormLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingUser ? 'Guardar cambios' : 'Crear usuario'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
